import React, { useEffect, useState } from "react";
import { InputGroup, Form } from "react-bootstrap";
import { ClipLoader } from "react-spinners";
import moment from "moment";
import { toast } from "react-toastify";
import SpecificAPI from "services/SpecificApi";
import { DepartmentCard } from "views/Components/DepartmentCard";
import DeleteModal from "views/Components/DeleteModal";
import AddEditDepartmentModal from "./AddEditDepartmentModal";
import ManagementHeader from "views/Components/ManagementHeader";
import CustomSelect from "views/Components/CustomSelect";
import { useDepartmentsStore } from "context/useDepartmentsStore";
import { useModulesStore } from "context/useModulesStore";
import localization from "services/localization";
import { FaSearch } from "react-icons/fa";
import { useUserStore } from "context/useUserStore";
import { getDepartmentTypes } from "services/constants";
import { DepartmentTable } from "./DepartmentTable";
import ViewToggle from "views/Components/ViewToggle";
import { useLanguageStore } from "context/useLanguageStore";

const DepartmentManagment = () => {
  const { userData, setUserAppData } = useUserStore();
  const { currentLang } = useLanguageStore();

  const emptyDepatment = {
    name: "",
    description: "",
    resources: [],
    type: "classroom",
    members: [],
    user_id: "",
    start_date: moment().format("YYYY-MM-DD"),
    end_date: moment().format("YYYY-MM-DD"),
    school_id: userData?.selected_school_id,
    module_id: [],
  };
  const [showAddEditItem, setShowAddEditItem] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState({
    value: "all",
    label: localization.all || "All",
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [itemName, setItemName] = useState("");
  const [users, setUsers] = useState([]);
  const [errors, setErrors] = useState({});
  const [editingItem, setEditingItem] = useState(emptyDepatment);
  const { departments, setDepartments } = useDepartmentsStore();
  const [filteredDepartments, setFilteredDepartments] = useState(departments);
  const [viewMode, setViewMode] = useState(userData?.department_view || "grid");
  const { modules } = useModulesStore();
  const DEPARTMENT_TYPE_OPTIONS = getDepartmentTypes();
  const ALL_DEPARTMENT_TYPE_OPTIONS = [
    { value: "all", label: localization.all || "All" },
    ...DEPARTMENT_TYPE_OPTIONS,
  ];

  useEffect(() => {
    getUserData();
  }, []);

  useEffect(() => {
    filterDepartments();
  }, [departments, searchTerm, selectedType]);

  const validateForm = (form) => {
    const e = {};

    if (!form.name || !form.name.trim()) {
      e.name = localization.name_required || "Name is required";
    }
    if (!form.type || !form.type.trim()) {
      e.type = localization.type_required || "Select a module type";
    }

    if (!form.user_id || form.user_id === "") {
      e.user_id = "Select a user";
    }

    return e;
  };

  const handleSubmit = async (data) => {
    const validationErrors = validateForm(data);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});

    try {
      setLoading(true);
      if (editingItem?.id) {
        const res = await SpecificAPI.updateDepartment(
          {
            ...data,
            _id: editingItem.id,
          },
          userData?.selected_school_id,
        );
        setDepartments((prev) =>
          prev.map((m) => (m.id === editingItem.id ? res.data : m)),
        );
      } else {
        const created = await SpecificAPI.createDepartment(data);
        setDepartments((prev) => [...prev, created.data]);
      }
      setShowAddEditItem(false);
      setEditingItem(emptyDepatment);

      toast.success(
        localization.department_saved || "Department saved successfully!",
      );
    } catch (err) {
      console.error("Error saving classroom:", err);
      toast.error(
        err?.response?.data?.error ||
          localization.failed_to_save_department ||
          "Failed to save department!",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setEditingItem((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleFormChange = (name, value) => {
    setEditingItem((prev) => {
      if (!prev) return { ...emptyPromptTemplate, [name]: value };
      return { ...prev, [name]: value };
    });

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };
  const getUserData = async () => {
    try {
      const response = await SpecificAPI.getAllUsers();
      setUsers(response || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const filterDepartments = () => {
    let filtered = departments;

    filtered = filtered.filter((d) => d.type !== "utilities");

    filtered = filtered.filter(
      (cls) => cls.school_id === userData?.selected_school_id,
    );
    if (searchTerm) {
      filtered = filtered.filter((cls) =>
        cls.name.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    if (selectedType.value !== "all") {
      filtered = filtered.filter(
        (cls) => cls.type?.toLowerCase() === selectedType.value.toLowerCase(),
      );
    }
    setFilteredDepartments(filtered);
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setShowAddEditItem(true);
    setErrors({});
  };

  const handleDelete = (item) => {
    setItemToDelete(item);
    setItemName(item.name || "");
    setShowDeleteModal(true);
  };
  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      setLoading(true);
      await SpecificAPI.deleteDepartment(
        itemToDelete.id,
        userData?.selected_school_id,
      );

      setDepartments((prev) => prev.filter((ev) => ev.id !== itemToDelete.id));

      toast.success("Department deleted successfully");
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Failed to delete department. Please try again.");
    } finally {
      setShowDeleteModal(false);
      setItemToDelete(null);
      setLoading(false);
    }
  };
  const handleCloseModal = () => {
    setShowAddEditItem(false);
    setEditingItem(emptyDepatment);
    setErrors({});
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    setUserAppData({
      ...userData,
      department_view: mode,
    });
  };

  return (
    <div className="d-flex flex-column p-8 mb-5 w-100 main-page">
      <div className="d-flex flex-column align-items-start mb-5 w-100 pr-2">
        <ManagementHeader
          title={localization.all_departments || "All Departments"}
          subtitle={
            localization.manage_your_departments || "Manage your departments"
          }
          // icon={MdViewModule}
          buttonText={localization.add_department || "Add Department"}
          onButtonClick={() => {
            setShowAddEditItem(true);
            setEditingItem(emptyDepatment);
            setErrors({});
          }}
          showBack={true}
          backTo="/schools"
          backLabel="Back to organization"
        />

        {/* Filters Row */}
        <div className="d-flex align-items-center gap-3 w-100 mt-4">
          {/* Search Input with Icon */}
          <InputGroup className="search-field flex-grow-1">
            <InputGroup.Text id="search-addon" className="customSearchAddon">
              <FaSearch className="icon-btn" />{" "}
            </InputGroup.Text>
            <Form.Control
              placeholder={
                localization.search_by_department_name ||
                "Search by department name..."
              }
              aria-label="Search"
              aria-describedby="search-addon"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="customSearchInput"
            />
          </InputGroup>

          {/* Grade Select */}
          <div style={{ flex: "0 0 250px" }}>
            <CustomSelect
              options={ALL_DEPARTMENT_TYPE_OPTIONS}
              value={selectedType}
              onChange={(opt) => setSelectedType(opt)}
              placeholder={localization.select_a_type || "Select a type"}
            />
          </div>
          <div className="d-flex align-items-center gap-2">
            <div className="d-flex align-items-center gap-2">
              <ViewToggle value={viewMode} onChange={handleViewModeChange} />
            </div>
          </div>
        </div>
      </div>

      {/* departments Grid */}
      {loading ? (
        <div className="flex justify-center items-center h-40">
          <ClipLoader size={40} color="var(--primary-icon)" />
        </div>
      ) : viewMode === "grid" ? (
        <div className="row g-4 w-100">
          {filteredDepartments.length > 0 ? (
            filteredDepartments.map((department) => (
              <div key={department.id} className="col-6 col-md-6 col-lg-4">
                <DepartmentCard
                  department={department}
                  onEdit={() => handleEditItem(department)}
                  onDelete={() => handleDelete(department)}
                  users={users}
                />
              </div>
            ))
          ) : (
            <p className="text-center text-muted">
              {localization.no_departments_found || "No departments found."}
            </p>
          )}
        </div>
      ) : (
        <DepartmentTable
          departments={filteredDepartments}
          users={users}
          onEdit={handleEditItem}
          onDelete={handleDelete}
        />
      )}
      <AddEditDepartmentModal
        show={showAddEditItem}
        title={
          editingItem.id
            ? localization.edit_department || "Edit Department"
            : localization.add_department || "Add Department"
        }
        formData={
          editingItem || {
            name: "",
            description: "",
            subject: "",
            grade: "",
            students: [],
          }
        }
        errors={errors}
        onChange={handleChange}
        onFormChange={handleFormChange}
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(editingItem);
        }}
        onClose={handleCloseModal}
        loading={loading}
        users={users}
        userData={userData}
        modules={modules}
      />
      <DeleteModal
        show={showDeleteModal}
        itemName={itemName}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        loading={loading}
        setLoading={setLoading}
      />
    </div>
  );
};

export default DepartmentManagment;
